<script lang="ts">
  import { onMount } from 'svelte'

  interface Props {
    fireHashid: string
    apiUrl: string
    sendUrl: string
    discardUrl: string
    csrfToken: string
    // When set (e.g. when embedded in a modal from the drafts list),
    // the component calls onComplete instead of navigating away.
    // When unset (full-page mount via /d/:hashid deeplink, including
    // from phone SMS), the existing window.location.assign behavior is used.
    onComplete?: (action: 'sent' | 'discarded') => void
  }

  let {
    fireHashid,
    apiUrl,
    sendUrl,
    discardUrl,
    csrfToken,
    onComplete,
  }: Props = $props()

  // --- API response shape ---
  interface TriggerFireData {
    id: string
    status: string
    fired_at_iso: string
    expires_at_iso: string
  }

  interface TriggerData {
    name: string
  }

  interface GroupData {
    name: string
    contact_count: number
  }

  interface MessageData {
    body: string
  }

  interface DraftResponse {
    trigger_fire: TriggerFireData
    trigger: TriggerData
    groups: GroupData[]
    unique_recipient_count: number
    message: MessageData
  }

  // --- State ---
  let loading = $state(true)
  let fetchError = $state<string | null>(null)

  let triggerFire = $state<TriggerFireData | null>(null)
  let trigger = $state<TriggerData | null>(null)
  let groups = $state<GroupData[]>([])
  let uniqueRecipientCount = $state(0)
  let messageBody = $state('')

  let submitting = $state(false)
  let submitError = $state<string | null>(null)

  let showSendConfirm = $state(false)
  let showDiscardConfirm = $state(false)

  const charCount = $derived(messageBody.length)

  // Audience descriptor — matches NotifyReviewerJob#audience_descriptor:
  // single group → group name; multi → "contacts across N groups".
  const audienceLabel = $derived(
    groups.length === 1 ? groups[0].name : `contacts across ${groups.length} groups`
  )

  const groupListLabel = $derived(
    groups.length === 0
      ? '—'
      : groups.length <= 2
        ? groups.map(g => g.name).join(', ')
        : `${groups[0].name}, ${groups[1].name}, +${groups.length - 2} more`
  )

  // --- Helpers ---
  function formatFiredAt(iso: string): string {
    try {
      const d = new Date(iso)
      const h = d.getHours()
      const m = d.getMinutes()
      const ampm = h >= 12 ? 'pm' : 'am'
      const hour12 = h % 12 || 12
      const mm = m.toString().padStart(2, '0')
      return `${hour12}:${mm}${ampm}`
    } catch {
      return iso
    }
  }

  function formatContactCount(n: number): string {
    return n.toLocaleString()
  }

  function isPendingReview(): boolean {
    return triggerFire?.status === 'pending_review'
  }

  function statusLabel(): string {
    const s = triggerFire?.status ?? ''
    const map: Record<string, string> = {
      approved: 'Approved',
      discarded: 'Discarded',
      expired: 'Expired',
      failed: 'Failed',
    }
    return map[s] ?? s
  }

  // --- Data fetch ---
  onMount(async () => {
    try {
      const res = await fetch(apiUrl, {
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken },
      })
      if (!res.ok) {
        fetchError = `Could not load draft (${res.status})`
        return
      }
      const data: DraftResponse = await res.json()
      triggerFire = data.trigger_fire
      trigger = data.trigger
      groups = data.groups ?? []
      uniqueRecipientCount = data.unique_recipient_count ?? 0
      messageBody = data.message.body
    } catch (e) {
      fetchError = 'Network error — please reload and try again.'
    } finally {
      loading = false
    }
  })

  // --- Actions ---
  async function confirmSend() {
    submitting = true
    submitError = null
    try {
      const res = await fetch(sendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ body: messageBody }),
      })
      const data = await res.json()
      if (res.ok) {
        if (onComplete) {
          onComplete('sent')
        } else {
          window.location.assign(data.redirect_url)
        }
      } else {
        submitError = data.error ?? 'Something went wrong. Please try again.'
        showSendConfirm = false
      }
    } catch {
      submitError = 'Network error — please try again.'
      showSendConfirm = false
    } finally {
      submitting = false
    }
  }

  async function confirmDiscard() {
    submitting = true
    submitError = null
    try {
      const res = await fetch(discardUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({}),
      })
      const data = await res.json()
      if (res.ok) {
        if (onComplete) {
          onComplete('discarded')
        } else {
          window.location.assign(data.redirect_url)
        }
      } else {
        submitError = data.error ?? 'Something went wrong. Please try again.'
        showDiscardConfirm = false
      }
    } catch {
      submitError = 'Network error — please try again.'
      showDiscardConfirm = false
    } finally {
      submitting = false
    }
  }
</script>

<div class="DraftReview">
  {#if loading}
    <div class="dr-loading" aria-busy="true" aria-label="Loading draft...">
      <div class="dr-spinner"></div>
      <p>Loading draft...</p>
    </div>

  {:else if fetchError}
    <div class="dr-error-banner" role="alert">
      <p>{fetchError}</p>
      <button class="dr-btn dr-btn--secondary" onclick={() => window.location.reload()}>
        Reload
      </button>
    </div>

  {:else if triggerFire && trigger && groups.length > 0}

    {#if !isPendingReview()}
      <div
        class="dr-status-banner"
        class:dr-status-banner--approved={triggerFire.status === 'approved'}
        class:dr-status-banner--discarded={triggerFire.status === 'discarded'}
        class:dr-status-banner--expired={triggerFire.status === 'expired'}
        class:dr-status-banner--failed={triggerFire.status === 'failed'}
        role="status"
      >
        <div class="dr-status-banner__text">
          This draft has already been <strong>{statusLabel()}</strong>.
          {#if triggerFire.status === 'approved'}
            The message was sent successfully.
          {:else if triggerFire.status === 'discarded'}
            The draft was discarded.
          {:else if triggerFire.status === 'expired'}
            The review window has closed.
          {/if}
        </div>
        <a class="dr-status-banner__back" href="/triggers">Back to Triggers →</a>
      </div>
    {/if}

    <div class="dr-meta">
      <div class="dr-meta-row">
        <span class="dr-label">Trigger</span>
        <span class="dr-value">{trigger.name}</span>
        <span class="dr-fired-at">{formatFiredAt(triggerFire.fired_at_iso)}</span>
      </div>
      <div class="dr-meta-row">
        <span class="dr-label">Sending to</span>
        <span class="dr-value" title={groups.map(g => g.name).join(', ')}>
          {groupListLabel} &middot; {formatContactCount(uniqueRecipientCount)} {uniqueRecipientCount === 1 ? 'contact' : 'contacts'}
        </span>
      </div>
    </div>

    <div class="dr-compose">
      <label class="dr-compose-label" for="draft-body">
        Message
        {#if !isPendingReview()}
          <span class="dr-readonly-badge">read-only</span>
        {/if}
      </label>
      <textarea
        id="draft-body"
        class="dr-textarea"
        rows={6}
        bind:value={messageBody}
        disabled={!isPendingReview() || submitting}
        aria-label="Draft message body"
        placeholder="Message body"
      ></textarea>

      <div class="dr-counter" aria-live="polite">
        {charCount} chars &middot; {formatContactCount(uniqueRecipientCount)} {uniqueRecipientCount === 1 ? 'credit' : 'credits'}
      </div>
    </div>

    {#if submitError}
      <div class="dr-error-banner" role="alert">
        {submitError}
      </div>
    {/if}

    {#if isPendingReview()}
      <div class="dr-actions">
        <button
          class="dr-btn dr-btn--primary"
          onclick={() => { submitError = null; showSendConfirm = true }}
          disabled={submitting || charCount === 0}
        >
          Send to {formatContactCount(uniqueRecipientCount)} {audienceLabel}
        </button>

        <button
          class="dr-btn dr-btn--ghost"
          onclick={() => { submitError = null; showDiscardConfirm = true }}
          disabled={submitting}
        >
          Discard
        </button>
      </div>
    {/if}

  {/if}
</div>

<!-- Send confirmation overlay -->
{#if showSendConfirm}
  <div class="dr-overlay" role="dialog" aria-modal="true" aria-labelledby="send-confirm-title">
    <div class="dr-sheet">
      <h2 id="send-confirm-title" class="dr-sheet-title">Send now?</h2>
      <p class="dr-sheet-body">
        This will send the message to <strong>{formatContactCount(uniqueRecipientCount)}</strong> {audienceLabel} immediately.
        This cannot be undone.
      </p>
      <div class="dr-sheet-actions">
        <button
          class="dr-btn dr-btn--primary"
          onclick={confirmSend}
          disabled={submitting}
        >
          {submitting ? 'Sending...' : `Send to ${formatContactCount(uniqueRecipientCount)} ${uniqueRecipientCount === 1 ? 'contact' : 'contacts'}`}
        </button>
        <button
          class="dr-btn dr-btn--ghost"
          onclick={() => showSendConfirm = false}
          disabled={submitting}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Discard confirmation overlay -->
{#if showDiscardConfirm}
  <div class="dr-overlay" role="dialog" aria-modal="true" aria-labelledby="discard-confirm-title">
    <div class="dr-sheet">
      <h2 id="discard-confirm-title" class="dr-sheet-title">Discard this draft?</h2>
      <p class="dr-sheet-body">
        The draft will be discarded and no message will be sent to {audienceLabel || 'the group'}.
      </p>
      <div class="dr-sheet-actions">
        <button
          class="dr-btn dr-btn--danger"
          onclick={confirmDiscard}
          disabled={submitting}
        >
          {submitting ? 'Discarding...' : 'Yes, discard'}
        </button>
        <button
          class="dr-btn dr-btn--ghost"
          onclick={() => showDiscardConfirm = false}
          disabled={submitting}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
{/if}

<style lang="scss">
  /* ── Layout ── */
  .DraftReview {
    max-width: 480px;
    margin: 0 auto;
    padding: 1.25rem 1rem 3rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color: #1a1a1a;
  }

  /* ── Loading ── */
  .dr-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    padding: 3rem 0;
    color: #666;

    p {
      margin: 0;
      font-size: 0.9rem;
    }
  }

  .dr-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e0e0e0;
    border-top-color: #4a90e2;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* ── Status banner ── */
  .dr-status-banner {
    border-radius: 8px;
    padding: 0.75rem 1rem;
    margin-bottom: 1.25rem;
    font-size: 0.9rem;
    line-height: 1.4;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .dr-status-banner__text {
    flex: 1 1 auto;
  }

  .dr-status-banner__back {
    color: inherit;
    text-decoration: none;
    font-weight: 600;
    white-space: nowrap;
    border-bottom: 1px solid currentColor;
  }
  .dr-status-banner__back:hover { opacity: 0.8; }

  /* Use :global so Svelte doesn't prune dynamically-toggled modifier classes */
  :global(.dr-status-banner--approved)  { background: #edf7ed; color: #2e7d32; border: 1px solid #a5d6a7; }
  :global(.dr-status-banner--discarded) { background: #fafafa; color: #555;    border: 1px solid #ddd; }
  :global(.dr-status-banner--expired)   { background: #fff8e1; color: #f57f17; border: 1px solid #ffe082; }
  :global(.dr-status-banner--failed)    { background: #fdecea; color: #c62828; border: 1px solid #ef9a9a; }

  /* ── Error banner ── */
  .dr-error-banner {
    background: #fdecea;
    border: 1px solid #ef9a9a;
    border-radius: 8px;
    color: #c62828;
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
    font-size: 0.9rem;

    p { margin: 0 0 0.5rem; }
  }

  /* ── Meta rows ── */
  .dr-meta {
    margin-bottom: 1.25rem;
  }

  .dr-meta-row {
    display: flex;
    align-items: baseline;
    gap: 0.4rem;
    font-size: 0.9rem;
    line-height: 1.6;
    flex-wrap: wrap;
  }

  .dr-label {
    color: #888;
    flex-shrink: 0;

    &::after { content: ':'; }
  }

  .dr-value {
    font-weight: 500;
    flex: 1;
    min-width: 0;
  }

  .dr-fired-at {
    color: #888;
    font-size: 0.82rem;
    margin-left: auto;
    flex-shrink: 0;
  }

  /* ── Compose area ── */
  .dr-compose {
    margin-bottom: 1.25rem;
  }

  .dr-compose-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.82rem;
    font-weight: 600;
    color: #555;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 0.4rem;
  }

  .dr-readonly-badge {
    font-size: 0.72rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    background: #e0e0e0;
    color: #666;
    border-radius: 4px;
    padding: 0.1em 0.45em;
  }

  .dr-textarea {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #d0d0d0;
    border-radius: 8px;
    padding: 0.75rem;
    font-size: 1rem;
    line-height: 1.5;
    font-family: inherit;
    resize: vertical;
    transition: border-color 0.15s;
    background: #fff;

    &:focus {
      outline: none;
      border-color: #4a90e2;
      box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.15);
    }

    &:disabled {
      background: #f5f5f5;
      color: #555;
      cursor: default;
    }
  }

  .dr-counter {
    margin-top: 0.4rem;
    font-size: 0.8rem;
    color: #888;
    text-align: right;
  }

  /* ── Actions ── */
  .dr-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  /* ── Buttons ── */
  .dr-btn {
    display: block;
    width: 100%;
    padding: 0.875rem 1rem;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    border: none;
    transition: opacity 0.15s, transform 0.1s;
    text-align: center;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &:not(:disabled):active {
      transform: scale(0.98);
    }
  }

  .dr-btn--primary {
    background: #2e7d32;
    color: #fff;

    &:not(:disabled):hover { background: #276128; }
  }

  .dr-btn--danger {
    background: #c62828;
    color: #fff;

    &:not(:disabled):hover { background: #b71c1c; }
  }

  .dr-btn--ghost {
    background: transparent;
    color: #555;
    border: 1px solid #ccc;

    &:not(:disabled):hover { background: #f5f5f5; }
  }

  .dr-btn--secondary {
    background: #f0f0f0;
    color: #333;

    &:not(:disabled):hover { background: #e5e5e5; }
  }

  /* ── Confirmation overlay ── */
  .dr-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 1000;
    padding: 0;
    animation: overlay-in 0.18s ease;

    @media (min-width: 480px) {
      align-items: center;
    }
  }

  @keyframes overlay-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  .dr-sheet {
    background: #fff;
    border-radius: 16px 16px 0 0;
    padding: 1.5rem 1.25rem 2rem;
    width: 100%;
    max-width: 480px;
    animation: sheet-up 0.2s cubic-bezier(0.22, 1, 0.36, 1);

    @media (min-width: 480px) {
      border-radius: 16px;
      padding: 1.5rem 1.5rem 1.75rem;
    }
  }

  @keyframes sheet-up {
    from { transform: translateY(24px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }

  .dr-sheet-title {
    font-size: 1.15rem;
    font-weight: 700;
    margin: 0 0 0.6rem;
    color: #1a1a1a;
  }

  .dr-sheet-body {
    font-size: 0.95rem;
    line-height: 1.5;
    color: #444;
    margin: 0 0 1.25rem;
  }

  .dr-sheet-actions {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }
</style>
